import { cn } from "@/shared/utils/classname-merger";
import { Button, Tooltip } from "@nextui-org/react";
import { motion, AnimatePresence } from "framer-motion";
import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { NavItemData, NavItemChild } from "../../types/sidebar";

interface AccordionMenuItemProps {
  data: NavItemData;
  isCollapsed: boolean;
}

const ChildNavItem = ({
  child,
  isActive,
  onClick,
}: {
  child: NavItemChild;
  isActive: boolean;
  onClick: () => void;
}) => {
  return (
    <button
      onClick={onClick}
      className={cn(
        "w-full text-left text-[14px] leading-5 h-nav-child",
        "py-2 pr-2",
        "pl-[17px]",
        "transition-colors duration-150",
        "font-normal text-[#71717A]",
        "hover:bg-[#F8FAFC] hover:text-[#11181C]",
        isActive && [
          "bg-[#F8FAFC]",
          "text-[#11181C] font-medium",
          "border-l-[2px] border-[#006FEE]",
          "ml-[-1px]",
          "rounded-r-[4px]",
        ],
      )}
    >
      {child.title}
    </button>
  );
};

export const AccordionMenuItem = ({
  data,
  isCollapsed,
}: AccordionMenuItemProps) => {
  const location = useLocation();
  const { pathname } = location;
  const navigate = useNavigate();
  const { title, Icon, path, children } = data;

  // Extract base path (e.g., "third-party-risk" from "third-party-risk/home")
  const basePath = path.split("/")[0];

  // Check if current route matches parent base path OR any child path
  const isActive =
    pathname.includes(basePath) ||
    children?.some((child) => pathname.includes(child.path)) ||
    false;

  // Find which child is active based on pathname match
  const activeChildIndex =
    children?.findIndex((child) => pathname.includes(child.path)) ?? -1;

  // Auto-expand when route matches
  const [isExpanded, setIsExpanded] = useState(isActive);

  // Update expanded state when route changes
  useEffect(() => {
    if (isActive) {
      setIsExpanded(true);
    } else {
      setIsExpanded(false);
    }
  }, [isActive]);

  const handleParentClick = () => {
    if (isCollapsed) {
      // In collapsed mode, navigate directly to first child
      if (children && children.length > 0) {
        navigate(children[0].path);
      } else {
        navigate(path);
      }
    } else {
      // In expanded mode, toggle accordion
      if (!isExpanded && children && children.length > 0) {
        // When expanding, navigate to first child
        navigate(children[0].path);
      }
      setIsExpanded(!isExpanded);
    }
  };

  const handleChildClick = (child: NavItemChild) => {
    navigate(child.path);
  };

  // Collapsed mode: show icon with tooltip listing children
  if (isCollapsed) {
    return (
      <Tooltip
        content={
          <div className="flex flex-col gap-1 p-1">
            <span className="font-medium text-sm">{title}</span>
            {children?.map((child) => (
              <span key={child.title} className="text-xs text-text-tertiary pl-2">
                {child.title}
              </span>
            ))}
          </div>
        }
        placement="right"
      >
        <Button
          onClick={handleParentClick}
          isIconOnly
          className={cn(
            "h-fit w-fit font-normal text-text-secondary bg-transparent rounded-[12px] px-[16px] py-[10px]",
            isActive && "text-brand-primary",
          )}
        >
          <Icon
            color={isActive ? "#006FEE" : "#62748E"}
            strokeWidth={isActive ? 2 : 1.5}
            className="min-h-[20px] min-w-[20px] h-[20px] w-[20px]"
          />
        </Button>
      </Tooltip>
    );
  }

  // Expanded mode: show accordion with children
  return (
    <div className="flex flex-col">
      {/* Parent row */}
      <button
        onClick={handleParentClick}
        className={cn(
          "flex items-center w-full",
          "h-nav-item p-nav-item gap-nav-gap",
          "transition-colors duration-150",
          "text-text-secondary font-normal",
          isActive && "text-brand-primary font-medium",
        )}
      >
        <Icon
          color={isActive ? "#006FEE" : "#62748E"}
          strokeWidth={isActive ? 2 : 1.5}
          className="min-h-[20px] min-w-[20px] h-[20px] w-[20px]"
        />
        <span className="text-[14px] leading-5">{title}</span>
      </button>

      {/* Children container with animation */}
      <AnimatePresence initial={false}>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.2, ease: "easeInOut" }}
            className="overflow-hidden"
          >
            <div className="flex flex-col mt-1 pl-[22px]">
              <div className="border-l border-[#E4E4E7] flex flex-col">
                {children?.map((child, index) => (
                  <ChildNavItem
                    key={child.title}
                    child={child}
                    isActive={activeChildIndex === index}
                    onClick={() => handleChildClick(child)}
                  />
                ))}
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default AccordionMenuItem;
